import { zipSync } from 'fflate'

import type { BrowserImageDownloadResult } from './clientImageDownload'
import {
  getPageLayoutDefinition,
  layoutCanvasSize,
  layoutSlotRect,
} from './comicPageLayout'
import { getPanelsOnPage } from './comicPages'
import type { ComicEpisode, ComicPage } from './comicTypes'
import { fetchRemoteImageBlob } from './remoteImageBlob'

const EXPORT_CELL_PX = 1024
const EXPORT_GAP_PX = 6

function sanitizeFilenamePart(raw: string): string {
  const s = raw.trim().slice(0, 80)
  return s.replace(/[/\\?*:|"<>]/g, '_').replace(/\s+/g, ' ') || 'page'
}

async function loadDrawableImage(src: string): Promise<HTMLImageElement> {
  const blob = await fetchRemoteImageBlob(src)
  const objectUrl = URL.createObjectURL(blob)
  try {
    const img = new Image()
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('无法读取图片'))
      img.src = objectUrl
    })
    return img
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('导出失败'))),
      'image/png',
      0.92,
    )
  })
}

async function downloadBlobFile(blob: Blob, filename: string): Promise<BrowserImageDownloadResult> {
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename.endsWith('.png') ? filename : `${filename}.png`
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    return 'blob_download'
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 400)
  }
}

async function downloadZipFile(data: Uint8Array, filename: string): Promise<BrowserImageDownloadResult> {
  const blob = new Blob([data as unknown as BlobPart], { type: 'application/zip' })
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename.endsWith('.zip') ? filename : `${filename}.zip`
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    return 'blob_download'
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 400)
  }
}

/** 本页是否有可导出的成图（整页图或任一格有图） */
export function pageHasExportableImage(episode: ComicEpisode, page: ComicPage): boolean {
  if (page.pageImageUrl?.trim()) return true
  if (page.pageCandidates?.some((c) => c.imageUrl?.trim())) return true
  return getPanelsOnPage(episode, page).some((p) => Boolean(p.imageUrl?.trim()))
}

async function composePageFromPanels(
  episode: ComicEpisode,
  page: ComicPage,
): Promise<{ canvas: HTMLCanvasElement; width: number; height: number }> {
  const panels = getPanelsOnPage(episode, page)
  const drawable = panels.filter((p) => p.imageUrl?.trim())
  if (drawable.length === 0) {
    throw new Error('本页尚无可用成图')
  }

  const def = getPageLayoutDefinition(page.layout)
  const images: (HTMLImageElement | null)[] = []

  for (let i = 0; i < def.slots.length; i += 1) {
    const url = panels[i]?.imageUrl?.trim()
    if (!url) {
      images.push(null)
      continue
    }
    images.push(await loadDrawableImage(url))
  }

  const filled = images.filter(Boolean)
  if (filled.length === 0) {
    throw new Error('本页尚无可用成图')
  }

  const { width, height } = layoutCanvasSize(
    page.layout,
    EXPORT_CELL_PX,
    EXPORT_CELL_PX,
    EXPORT_GAP_PX,
  )
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('导出失败')

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, width, height)

  for (let i = 0; i < def.slots.length; i += 1) {
    const img = images[i]
    if (!img) continue
    const { x, y, w, h } = layoutSlotRect(
      def.slots[i]!,
      EXPORT_CELL_PX,
      EXPORT_CELL_PX,
      EXPORT_GAP_PX,
    )
    ctx.drawImage(img, x, y, w, h)
  }

  return { canvas, width, height }
}

/** 渲染单页 PNG Blob */
export async function renderComicPagePngBlob(
  episode: ComicEpisode,
  page: ComicPage,
): Promise<Blob> {
  const whole = page.pageImageUrl?.trim()
  if (whole) {
    const img = await loadDrawableImage(whole)
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth ?? img.width
    canvas.height = img.naturalHeight ?? img.height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('导出失败')
    ctx.drawImage(img, 0, 0)
    return canvasToPngBlob(canvas)
  }

  return composePageFromPanelsBlob(episode, page)
}

async function composePageFromPanelsBlob(
  episode: ComicEpisode,
  page: ComicPage,
): Promise<Blob> {
  const { canvas } = await composePageFromPanels(episode, page)
  return canvasToPngBlob(canvas)
}

/** 导出单页 PNG；有整页成图时直接下载，否则按嵌板拼接各格成图 */
export async function exportComicPage(
  episode: ComicEpisode,
  page: ComicPage,
  nameParts: string[],
): Promise<BrowserImageDownloadResult> {
  const base = sanitizeFilenamePart(nameParts.filter(Boolean).join('-'))
  const filename = `${base}-${sanitizeFilenamePart(page.title)}`
  const blob = await renderComicPagePngBlob(episode, page)
  return downloadBlobFile(blob, filename)
}

/** 将话内所有可导出页打包为单个 ZIP */
export async function exportComicEpisodeAsZip(
  episode: ComicEpisode,
  nameParts: string[],
): Promise<{ exported: number; skipped: number }> {
  const base = sanitizeFilenamePart(nameParts.filter(Boolean).join('-'))
  const { zipEntries, exported, skipped } = await collectEpisodePagesIntoZip(
    [episode],
    (_ep, page) =>
      `${String(page.index).padStart(2, '0')}-${sanitizeFilenamePart(page.title)}.png`,
  )

  if (exported === 0) {
    throw new Error('本话没有可导出的成图')
  }

  const zipData = zipSync(zipEntries, { level: 6 })
  await downloadZipFile(zipData, `${base}-${sanitizeFilenamePart(episode.title)}`)
  return { exported, skipped }
}

async function collectEpisodePagesIntoZip(
  episodes: ComicEpisode[],
  pathForPage: (episode: ComicEpisode, page: ComicPage) => string,
): Promise<{
  zipEntries: Record<string, Uint8Array>
  exported: number
  skipped: number
}> {
  const zipEntries: Record<string, Uint8Array> = {}
  let exported = 0
  let skipped = 0

  for (const episode of episodes) {
    for (const page of episode.pages) {
      if (!pageHasExportableImage(episode, page)) {
        skipped += 1
        continue
      }
      const blob = await renderComicPagePngBlob(episode, page)
      const buf = new Uint8Array(await blob.arrayBuffer())
      zipEntries[pathForPage(episode, page)] = buf
      exported += 1
    }
  }

  return { zipEntries, exported, skipped }
}

/** 将多话（如画布勾选）可导出页打包为单个 ZIP，每话一个子目录 */
export async function exportComicEpisodesAsZip(
  episodes: ComicEpisode[],
  nameParts: string[],
): Promise<{ exported: number; skipped: number; episodeCount: number }> {
  if (episodes.length === 0) {
    throw new Error('请先勾选要导出的话次')
  }

  const { zipEntries, exported, skipped } = await collectEpisodePagesIntoZip(
    episodes,
    (ep, page) => {
      const epFolder = `${String(ep.index).padStart(2, '0')}-${sanitizeFilenamePart(ep.title)}`
      return `${epFolder}/${String(page.index).padStart(2, '0')}-${sanitizeFilenamePart(page.title)}.png`
    },
  )

  if (exported === 0) {
    throw new Error('所选话次没有可导出的成图')
  }

  const base = sanitizeFilenamePart(nameParts.filter(Boolean).join('-'))
  const zipData = zipSync(zipEntries, { level: 6 })
  await downloadZipFile(zipData, `${base}-多话导出`)
  return { exported, skipped, episodeCount: episodes.length }
}

async function pngBlobToDataUrl(blob: Blob): Promise<{
  dataUrl: string
  width: number
  height: number
}> {
  const bitmap = await createImageBitmap(blob)
  try {
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('导出失败')
    ctx.drawImage(bitmap, 0, 0)
    return {
      dataUrl: canvas.toDataURL('image/png'),
      width: canvas.width,
      height: canvas.height,
    }
  } finally {
    bitmap.close()
  }
}

/** 将话内可导出页合并为单个 PDF（每页一页） */
export async function exportComicEpisodeAsPdf(
  episode: ComicEpisode,
  nameParts: string[],
): Promise<{ exported: number; skipped: number }> {
  const exportable = episode.pages.filter((p) => pageHasExportableImage(episode, p))
  const skipped = episode.pages.length - exportable.length
  if (exportable.length === 0) {
    throw new Error('本话没有可导出的成图')
  }

  const { jsPDF } = await import('jspdf')
  let pdf: InstanceType<typeof jsPDF> | null = null

  for (const page of exportable) {
    const blob = await renderComicPagePngBlob(episode, page)
    const { dataUrl, width, height } = await pngBlobToDataUrl(blob)
    const orientation = width >= height ? 'landscape' : 'portrait'
    if (!pdf) {
      pdf = new jsPDF({ orientation, unit: 'px', format: [width, height] })
      pdf.addImage(dataUrl, 'PNG', 0, 0, width, height)
    } else {
      pdf.addPage([width, height], orientation)
      pdf.addImage(dataUrl, 'PNG', 0, 0, width, height)
    }
  }

  const base = sanitizeFilenamePart(nameParts.filter(Boolean).join('-'))
  pdf!.save(`${base}-${sanitizeFilenamePart(episode.title)}.pdf`)
  return { exported: exportable.length, skipped }
}

export async function exportComicEpisode(
  episode: ComicEpisode,
  nameParts: string[],
): Promise<{ exported: number; skipped: number }> {
  let exported = 0
  let skipped = 0
  for (const page of episode.pages) {
    if (!pageHasExportableImage(episode, page)) {
      skipped += 1
      continue
    }
    await exportComicPage(episode, page, nameParts)
    exported += 1
    if (exported < episode.pages.length) {
      await new Promise((r) => window.setTimeout(r, 320))
    }
  }
  if (exported === 0) {
    throw new Error('本话没有可导出的成图')
  }
  return { exported, skipped }
}
